import uuid from 'uuid/v4';
import { AVAILABILITY_STATUS } from '@wix/chat-web';
import { availabilityHours } from '@wix/inbox-common';
import { ChatInitResult } from '../services/serverApi';
import { ThunkExtraArgument } from '../store/configureStore';
import { initFirebase } from './firebase';
import {
  initChatSdk,
  updateBusinessChatRoomId,
  updateShardId,
  updateCorrelationId,
  updateIsChatSdkInit,
  updateCoreChatToken,
  updateAvailabilityStatus,
  updateIsBranded,
  updateAllowInput,
} from './index';
import wix from '../services/wix';
import firebaseChatService from '../services/firebaseChatService';
import { EDITOR_VIEW_MODE } from '../constants/editor-view-mode';
import { isPremium } from '../services/premium';
import { loadingScheduler } from '../services/loading-scheduler';

const trackAvailabilitySchedule = (availability, onChange, lastValue?) => {
  if (!availability) {
    return;
  }

  const {
    isInPeriod,
    isFuturePeriodAvailable,
  } = availabilityHours.calculateAvailability(availability);

  if (lastValue !== isInPeriod) {
    const availabilityStatus = isInPeriod
      ? AVAILABILITY_STATUS.AVAILABLE
      : AVAILABILITY_STATUS.AWAY;
    onChange(availabilityStatus);
  }

  if (isFuturePeriodAvailable) {
    const delay = 60 * 1000;
    window.setTimeout(() => {
      trackAvailabilitySchedule(availability, onChange, isInPeriod);
    }, delay);
  }
};

export const initApp = ({
  reconnectData = undefined as any,
  isBusinessChat = true,
} = {}) => {
  return async function(
    dispatch,
    getState,
    { visitCount, serverApi }: ThunkExtraArgument,
  ) {
    const deviceType: string = getState().deviceType;
    const isInitialSiteModeInit =
      getState().experiments['specs.chat.InitChatInRender'] &&
      getState().viewMode === EDITOR_VIEW_MODE.SITE &&
      !reconnectData;
    const correlationId: string = isInitialSiteModeInit
      ? getState().chatInitResult.correlationId
      : uuid();
    const chatInitResult = isInitialSiteModeInit
      ? (getState().chatInitResult as ChatInitResult)
      : await serverApi.init({
          deviceType,
          backend: 'CHAT',
          correlationId,
          ...reconnectData,
        });
    const {
      derivedChatroomId,
      authKey,
      options,
      messagePath,
      presencePath,
      visitorId,
      visitorInstanceId,
      location,
      shardId,
      timestamp,
      coreChatToken,
      availability,
      isBranded /* NOT (chat-premium || grandfathered) */,
    } = chatInitResult;
    dispatch(updateBusinessChatRoomId(derivedChatroomId));
    dispatch(updateShardId(shardId));
    dispatch(updateCorrelationId(correlationId));
    dispatch(updateCoreChatToken(coreChatToken));

    trackAvailabilitySchedule(availability, availabilityStatus => {
      dispatch(updateAvailabilityStatus(availabilityStatus));
    });

    const initSdk = () => {
      dispatch(initChatSdk({ coreChatToken, isBusinessChat }));
      dispatch(updateIsChatSdkInit(true));
    };

    void loadingScheduler.onConnective().then(async () => {
      if (getState().widgetMode !== 'site') {
        initSdk();
      }

      if (!reconnectData) {
        await visitCount.incVisitCount();
      }

      dispatch(
        initFirebase({
          initData: {
            authKey,
            options,
            messagePath,
            presencePath,
            visitorInstanceId,
            correlationId,
            visitorId,
          },
          deviceType,
          location,
          timestamp,
          derivedChatroomId,
        }),
      );

      if (!reconnectData) {
        [
          'syncPageTitle',
          'subscribeForNavigation',
          'subscribeForMetadataChange',
        ].forEach(f =>
          wix[f](pageName => {
            firebaseChatService.updatePresence({ pageName });
          }),
        );
      }
    });

    void loadingScheduler.onInteractive().then(async () => {
      if (getState().widgetMode === 'site') {
        initSdk();
      }

      if (isBranded !== undefined) {
        const isChatPremium = !isBranded;

        if (isChatPremium) {
          dispatch(updateIsBranded(isBranded));
        } else {
          isPremium()
            .then(_isPremium => {
              const isFreePackage = !_isPremium;
              dispatch(updateIsBranded(isFreePackage));
            })
            .catch(() => {
              /* do nothing */
            });
        }
      }

      if (!reconnectData) {
        const { isInPeriod } = availabilityHours.calculateAvailability(
          availability,
        );
        const isAvailable = isInPeriod || !availability;

        const { isMandatory, isContact } = getState().isLcfMandatory;
        dispatch(updateAllowInput(isContact || (isAvailable && !isMandatory)));
      }
    });
  };
};
